import { gql, useQuery } from "utils/urql";
import { useState } from "react";
import { BooksQuery, BooksQueryVariables } from "types/api";
import cx from "classnames";
import styles from "./ArticleBooksModule.module.scss";
import Image from "components/Image";
import { useArticleContext } from "utils/articleContext";
import useMedia from "utils/useMedia";
import ArticleBooksDropdown from "./ArticleBooksDropdown";
import {
  openAndCloseMenus,
  closeAllMenus,
  useEscape,
} from "utils/bookDropdown";

const BOOKS_QUERY = gql`
  query BooksQuery($id: ID!, $preview: String) {
    article(id: $id, preview: $preview) {
      ... on Article {
        reviews {
          __typename
          title
          creator
          ... on BookReview {
            bookshopIsbn
            asin
            publisher
            image(height: 120, sizes: ["1x", "2x"]) {
              url
              width
              height
              srcSet
              altText
            }
          }
        }
      }
    }
  }
`;

const ArticleBooksModule = () => {
  const { articleId, previewParam } = useArticleContext();
  const [{ data }] = useQuery<BooksQuery, BooksQueryVariables>({
    query: BOOKS_QUERY,
    variables: {
      id: articleId,
      preview: previewParam,
    },
  });

  const books = data?.article.reviews?.filter(
    (review) => review.__typename === "BookReview"
  );

  const initialDropdownState: boolean[] = [];
  books && books?.forEach(() => initialDropdownState.push(false));
  const [openDropdown, setOpenDropdown] = useState(initialDropdownState);

  const [isMobile] = useMedia("(max-width: 576px)");
  useEscape(openDropdown, setOpenDropdown);

  if (!books || !books.length) return null;

  const fullWidth = books.length > 2;
  const isDropdownInsetOrMobile = !fullWidth || isMobile;

  return (
    <section className={styles.root}>
      {/* minHeight gives the inset placeholder height before the content loads -- less shifting around */}
      <div
        className={cx(fullWidth ? styles.fullWidth : styles.inset)}
        style={fullWidth ? {} : { minHeight: `${61 + books.length * 176}px` }}
      >
        {books.map((book, i) => {
          return (
            book.__typename === "BookReview" &&
            i < 3 && (
              <div
                className={styles.book}
                key={`book-${i}`}
                data-view-action="view link - inline affiliate promo"
                data-view-label={book.title}
              >
                {book.image && (
                  <picture
                    className={cx(
                      styles.picture,
                      fullWidth && styles.fullWidthPicture
                    )}
                  >
                    <Image
                      lazy={true}
                      className={styles.image}
                      width={book.image.width}
                      height={book.image.height}
                      alt={book.image.altText || ""}
                      src={book.image.url}
                      srcSet={book.image.srcSet}
                    />
                  </picture>
                )}
                <div className={cx(fullWidth && styles.fullWidthWrapper)}>
                  <div
                    className={cx(
                      styles.textWrapper,
                      fullWidth && styles.fullWidthTextWrapper
                    )}
                  >
                    <span className={styles.title}>{book.title}</span>
                    {book.creator && (
                      <span
                        className={cx(
                          styles.creator,
                          fullWidth && styles.fullWidthCreator
                        )}
                      >{`${book.creator}${book.publisher && ", "}`}</span>
                    )}
                    {book.publisher && (
                      <span
                        className={cx(
                          styles.publisher,
                          fullWidth && styles.fullWidthPublisher
                        )}
                      >
                        {book.publisher}
                      </span>
                    )}
                  </div>

                  <ArticleBooksDropdown
                    toggleMenu={() =>
                      openAndCloseMenus(openDropdown, setOpenDropdown, i)
                    }
                    blurFunc={() => {
                      closeAllMenus(openDropdown, setOpenDropdown);
                    }}
                    showDrop={openDropdown[i]}
                    index={i}
                    modulePosition="inline"
                    insetOrMobile={isDropdownInsetOrMobile}
                    {...book}
                  />
                </div>
              </div>
            )
          );
        })}
        <div className={styles.disclosure}>
          When you buy a book using a link on this page, we receive a
          commission. Thank you for supporting <i>The Atlantic</i>.
        </div>
      </div>
    </section>
  );
};

export default ArticleBooksModule;
